import { Button, Grid } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import ExportToCsvButton from 'common/components/ExportToCsvButton';
import WorkQueues from 'enums/workQueue.enum';
import {
  filterPrescriptionsNeedsReview,
  getPrescriptionsNeedsReviewByPage,
} from 'store/thunks/workQueueThunks';

import { useWorkQueueTabSwitch } from '../contexts/WorkQueueTabSwitchContext';
import BulkCancelPrescriptionButton from './BulkCancelPrescriptionButton';
import BulkTagPrescriptionButton from './BulkTagPrescriptionButton';
import PrescriptionReviewFilterButton from './PrescriptionReviewFilterButton';
import PrescriptionReviewFilterStatusBar from './PrescriptionReviewFilterStatusBar';
import {
  PrescriptionReviewSelectionProvider,
  usePrescriptionReviewSelection,
} from './PrescriptionReviewSelectionContext';
import PrescriptionReviewTable from './PrescriptionReviewTable';

const URGENT_TAG = 'URGENT';

const PrescriptionReviewContent = () => {
  const dispatch = useDispatch();
  const { clearSelectedItems } = usePrescriptionReviewSelection();
  const { filter, currentPage } = useSelector(
    ({ workQueue }) => workQueue.prescriptionReview,
    shallowEqual
  );
  const { isTabSwitch } = useWorkQueueTabSwitch();

  useEffect(() => {
    if (isTabSwitch(WorkQueues.PRESCRIPTION_REVIEW)) {
      dispatch(getPrescriptionsNeedsReviewByPage({ page: currentPage }));
    }
  }, [dispatch, currentPage, isTabSwitch]);

  const reloadPageWithLoading = useCallback(() => {
    clearSelectedItems();
    dispatch(filterPrescriptionsNeedsReview({ filter }));
  }, [dispatch, filter, clearSelectedItems]);

  const isUrgentFilterActive = filter?.tags?.includes(URGENT_TAG);

  const handleUrgentFilter = useCallback(() => {
    const currentTags = filter?.tags || [];
    const newTags = isUrgentFilterActive
      ? currentTags.filter((t) => t !== URGENT_TAG)
      : [...currentTags, URGENT_TAG];
    dispatch(
      filterPrescriptionsNeedsReview({
        filter: {
          ...filter,
          tags: newTags.length > 0 ? newTags : undefined,
          // POM-6330: the Urgent view is always Exact, even if Tags had been switched to Contains.
          tagMatchMode: undefined,
        },
      })
    );
  }, [dispatch, filter, isUrgentFilterActive]);

  return (
    <Grid container sx={{ py: 2 }}>
      <Grid item sx={{ minHeight: 40, flexGrow: 1 }} alignItems='center'>
        <PrescriptionReviewFilterStatusBar />
      </Grid>
      <Grid item>
        <Grid container gap={1}>
          <Button
            variant={isUrgentFilterActive ? 'contained' : 'outlined'}
            color='info'
            onClick={handleUrgentFilter}
            sx={{ height: 33 }}
          >
            Urgent
          </Button>
          <BulkCancelPrescriptionButton onSuccess={reloadPageWithLoading} />
          <BulkTagPrescriptionButton onSuccess={reloadPageWithLoading} />
          <PrescriptionReviewFilterButton />
          <ExportToCsvButton />
        </Grid>
      </Grid>
      <PrescriptionReviewTable />
    </Grid>
  );
};

const PrescriptionReview = () => {
  const preset = useSelector(({ workQueue }) => workQueue.preset);

  if (preset !== WorkQueues.PRESCRIPTION_REVIEW) return null;

  return (
    <PrescriptionReviewSelectionProvider>
      <PrescriptionReviewContent />
    </PrescriptionReviewSelectionProvider>
  );
};

export default PrescriptionReview;
